import { Catalog } from '@df/catalog/catalog.interfaces';
import { AbstractFinder } from '@df/catalog/abstract-finder/abstract-finder';
import { getOpeningHours } from '@df/catalog/store/get-opening-hours';
import { getTodayOpeningHours } from '@df/catalog/store/get-today-opening-hours';
import { GMTDateToUK } from '@df/util/fn/uk-date';

export declare type IStoreData = IStoreDataV1 | IStoreDataV3;

export interface IStoreDataV1 extends Catalog.IFinderDataV1 {
    store_name: string;
    allow_for_checkout?: 1 | 0;
    allow_for_finder?: 1 | 0;
    city?: string;
    days?: IDaysData;
    postcode?: string;
    address?: string;
    phone?: string;
    state?: string;
    description?: string;
    url?: string;
    email?: string;
    sort_order?: number;
    heading?: number;
    pitch?: number;
    fov?: number;
    country_id?: string;
}

export interface IStoreDataV3 extends IStoreDataV1, Catalog.IFinderDataV3 {
    tags?: { tag_id: number; tag_name: string }[];
}

export interface IDayData {
    open: string;
    close: string;
    open_break: string;
    close_break: string;
    status: 1 | 2;
    holiday?: boolean;
    special_day?: boolean;
}

export interface IDaysData {
    [data: string]: IDayData;
}

export interface IDay extends IDayData {
    date?: Date;
}

export interface IDays {
    [date: string]: IDay;
}

export class Store<T extends IStoreData = IStoreData> extends AbstractFinder<T> {
    /**
     * Object type id
     * @type {String}
     */
    static override type = 'store';

    /**
     * If model is routable
     * @type {Boolean}
     */
    static override routable = true;

    protected _openingHours?: IDay[];
    protected _todayOpeningHours?: IDay;

    override setData(resultData: T) {
        const data = resultData as IStoreDataV3;
        if (data.tags && !Array.isArray(data.tags)) {
            data.tags = Object.values(data.tags);
        }
        super.setData(resultData);
    }

    get todayOpeningHours(): IDay | undefined {
        if (this.openingHours && this._todayOpeningHours === undefined) {
            const day = getTodayOpeningHours(this.openingHours);

            if (day) {
                this._todayOpeningHours = day;
            }
        }
        return this._todayOpeningHours;
    }

    get openingHours(): IDay[] | undefined {
        if (!this._openingHours) {
            if (this.data && this.data.days) {
                this._openingHours = getOpeningHours(this.data.days);
            }
        }
        return this._openingHours;
    }

    /**
     * If store is open now
     * @todo review, logic copied from local code
     */
    get isOpenNow(): boolean {
        const hours = this.todayOpeningHours;
        if (this.isClosed(hours)) {
            return false;
        }
        const date = new Date();
        GMTDateToUK(date);
        const h = (date.getUTCHours() + '').padStart(2, '0') + ':' + (date.getUTCMinutes() + '').padStart(2, '0');
        return hours?.status === 1 && hours.open <= h && hours.close > h;
    }

    /**
     * @todo review, logic copied from local code
     */
    get isOpenToday(): boolean {
        return !this.isClosed(this.todayOpeningHours);
    }

    isClosed(day?: IDay): boolean {
        return !day || day.status === 2 || day.holiday || day.open === 'CLOSED' || !day.open;
    }

    /**
     * Fix for JSON.stringify getters
     */
    toJSON() {
        const { directions, getDistance, distance, todayOpeningHours, openingHours } = this;
        return {
            directions,
            getDistance,
            distance,
            todayOpeningHours,
            openingHours
        };
    }

    protected override getName() {
        return this.data?.store_name;
    }
}
